import {
  getMemberDobTimestamp,
  getMemberDeathYearNumber,
  getMemberDeathMonthDayOrder,
} from "./family-tree/dateAndTime";
import {
  formatDashboardMemberDisplayName,
  isDashboardInLawMember,
} from "./dashboardMemberDisplayName";
import { isDashboardStartOrTopOfLineMember } from "./dashboardStartOfLineMember";

export function sortDashboardInLawsAlphabetically(members, nameOptions) {
  return [...members].sort((a, b) => {
    const nameA = formatDashboardMemberDisplayName(a, nameOptions).toLowerCase();
    const nameB = formatDashboardMemberDisplayName(b, nameOptions).toLowerCase();
    return nameA.localeCompare(nameB);
  });
}

export function getDashboardMemberLineKey(member) {
  const key = member?.id ?? member?.member_id;
  return key != null ? String(key) : null;
}

function getMultipleBirthOrderNumber(member) {
  const rawOrder = member?.multiple_birth_order_number;
  if (rawOrder == null || rawOrder === "") return null;

  const parsedOrder =
    typeof rawOrder === "string" ? parseInt(rawOrder, 10) : rawOrder;

  return Number.isFinite(parsedOrder) ? parsedOrder : null;
}

export function compareDashboardMembersByOldest(a, b) {
  const dobA = getMemberDobTimestamp(a);
  const dobB = getMemberDobTimestamp(b);

  if (dobA === -1 && dobB === -1) return 0;
  if (dobA === -1) return 1;
  if (dobB === -1) return -1;

  if (dobA !== dobB) return dobA - dobB;

  const multipleBirthOrderA = getMultipleBirthOrderNumber(a);
  const multipleBirthOrderB = getMultipleBirthOrderNumber(b);

  if (multipleBirthOrderA != null && multipleBirthOrderB != null) {
    return multipleBirthOrderA - multipleBirthOrderB;
  }
  if (multipleBirthOrderA != null) return -1;
  if (multipleBirthOrderB != null) return 1;

  return 0;
}

export function compareDashboardMembersByYoungest(a, b) {
  return compareDashboardMembersByOldest(b, a);
}

export function compareDashboardMembersByDeathDate(a, b, nameOptions) {
  const compareByName = () => {
    const nameA = formatDashboardMemberDisplayName(a, nameOptions).toLowerCase();
    const nameB = formatDashboardMemberDisplayName(b, nameOptions).toLowerCase();
    return nameA.localeCompare(nameB);
  };

  const yearA = getMemberDeathYearNumber(a);
  const yearB = getMemberDeathYearNumber(b);

  // Members whose death year is unknown sink to the bottom.
  if (yearA == null && yearB == null) return compareByName();
  if (yearA == null) return 1;
  if (yearB == null) return -1;

  // Primary order is the (displayed) death year, oldest first.
  if (yearA !== yearB) return yearA - yearB;

  // Within the same year, order by month/day so the first to die is listed
  // first. Members with only year precision fall after those with a full date.
  const monthDayA = getMemberDeathMonthDayOrder(a);
  const monthDayB = getMemberDeathMonthDayOrder(b);
  if (monthDayA != null && monthDayB != null && monthDayA !== monthDayB) {
    return monthDayA - monthDayB;
  }
  if (monthDayA != null && monthDayB == null) return -1;
  if (monthDayA == null && monthDayB != null) return 1;

  return compareByName();
}

export function sortDashboardMembersByDeathDate(members, nameOptions) {
  return [...members].sort((a, b) =>
    compareDashboardMembersByDeathDate(a, b, nameOptions)
  );
}

export function sortDashboardMembersOldestToYoungest(members) {
  return [...members].sort(compareDashboardMembersByOldest);
}

/**
 * Start/Top of Line members are numbered 1..n (oldest first), then descendants
 * continue n+1.. (oldest first). In-laws are not numbered.
 */
export function buildDashboardFamilyLineNumberMap(members) {
  const map = new Map();
  if (!Array.isArray(members) || members.length === 0) return map;

  const startTop = sortDashboardMembersOldestToYoungest(
    members.filter((member) => isDashboardStartOrTopOfLineMember(member))
  );
  const descendants = sortDashboardMembersOldestToYoungest(
    members.filter(
      (member) =>
        !isDashboardStartOrTopOfLineMember(member) &&
        !isDashboardListedInLawMember(member)
    )
  );

  let lineNumber = 1;
  for (const member of startTop) {
    const key = getDashboardMemberLineKey(member);
    if (key) map.set(key, lineNumber++);
  }
  for (const member of descendants) {
    const key = getDashboardMemberLineKey(member);
    if (key) map.set(key, lineNumber++);
  }

  return map;
}

export function getDashboardFamilyLineNumber(member, lineNumberMap) {
  const key = getDashboardMemberLineKey(member);
  if (!key || !lineNumberMap) return null;
  return lineNumberMap.get(key) ?? null;
}

export function isDashboardListedInLawMember(member) {
  return (
    isDashboardInLawMember(member) &&
    !isDashboardStartOrTopOfLineMember(member)
  );
}

function splitDashboardMembersForInLawsLast(
  members,
  { isListedInLaw = isDashboardListedInLawMember, separateStartTop = false } = {}
) {
  const startTop = [];
  const descendants = [];
  const inLaws = [];

  for (const member of members) {
    if (isListedInLaw(member)) {
      inLaws.push(member);
    } else if (separateStartTop && isDashboardStartOrTopOfLineMember(member)) {
      startTop.push(member);
    } else {
      descendants.push(member);
    }
  }

  return { startTop, descendants, inLaws };
}

function compareDashboardMembersByFamilyLineNumber(a, b, lineNumberMap, nameOptions) {
  const numA = getDashboardFamilyLineNumber(a, lineNumberMap);
  const numB = getDashboardFamilyLineNumber(b, lineNumberMap);
  if (numA == null && numB == null) return 0;
  if (numA == null) return 1;
  if (numB == null) return -1;
  if (numA !== numB) return numA - numB;

  const nameA = formatDashboardMemberDisplayName(a, nameOptions).toLowerCase();
  const nameB = formatDashboardMemberDisplayName(b, nameOptions).toLowerCase();
  return nameA.localeCompare(nameB);
}

/**
 * Default Total Family Members order: numbered members by family line # (1, 2, 3…),
 * then the in-laws at the end A–Z by display name — they carry no line number,
 * so a name list is what the tail is read by.
 */
export function sortDashboardMembersByFamilyLineNumberOrder(
  members,
  memberFilter = "all",
  options = {}
) {
  if (memberFilter !== "all" || !Array.isArray(members) || members.length <= 1) {
    return members;
  }

  const {
    isListedInLaw = isDashboardListedInLawMember,
    nameOptions,
    lineNumberMap = buildDashboardFamilyLineNumberMap(members),
  } = options;

  const { startTop, descendants, inLaws } = splitDashboardMembersForInLawsLast(
    members,
    { isListedInLaw }
  );

  const compareByLineNumber = (a, b) =>
    compareDashboardMembersByFamilyLineNumber(a, b, lineNumberMap, nameOptions);

  const numberedMembers = [...startTop, ...descendants].sort(compareByLineNumber);

  if (inLaws.length === 0) return numberedMembers;

  return [
    ...numberedMembers,
    ...sortDashboardInLawsAlphabetically(inLaws, nameOptions),
  ];
}

/** When viewing all members, keep in-laws at the end, sorted A–Z by display name. */
export function applyDashboardInLawsLastOrder(
  members,
  memberFilter = "all",
  options = {}
) {
  if (memberFilter !== "all" || !Array.isArray(members) || members.length <= 1) {
    return members;
  }

  const {
    isListedInLaw = isDashboardListedInLawMember,
    separateStartTop = false,
    nameOptions,
  } = options;
  const { startTop, descendants, inLaws } = splitDashboardMembersForInLawsLast(
    members,
    { isListedInLaw, separateStartTop }
  );

  if (inLaws.length === 0) return members;

  if (separateStartTop) {
    return [
      ...startTop,
      ...descendants,
      ...sortDashboardInLawsAlphabetically(inLaws, nameOptions),
    ];
  }

  const nonInLaws = members.filter((member) => !isListedInLaw(member));
  return [
    ...nonInLaws,
    ...sortDashboardInLawsAlphabetically(inLaws, nameOptions),
  ];
}

/**
 * Sort each group (non in-laws, then in-laws) with `sortFn`, concatenating in-laws last.
 * `sortFn` receives a copy of the array and must return a sorted array.
 */
export function sortDashboardMembersWithInLawsLast(
  members,
  sortFn,
  memberFilter = "all",
  options = {}
) {
  if (memberFilter !== "all" || !Array.isArray(members) || members.length <= 1) {
    return sortFn([...members]);
  }

  const {
    isListedInLaw = isDashboardListedInLawMember,
    separateStartTop = false,
    nameOptions,
    useSortFnForInLaws = false,
  } = options;
  const { startTop, descendants, inLaws } = splitDashboardMembersForInLawsLast(
    members,
    { isListedInLaw, separateStartTop }
  );

  if (inLaws.length === 0) return sortFn([...members]);

  const sortInLaws = (list) =>
    useSortFnForInLaws
      ? sortFn([...list])
      : sortDashboardInLawsAlphabetically(list, nameOptions);

  if (separateStartTop) {
    return [
      ...sortFn(startTop),
      ...sortFn(descendants),
      ...sortInLaws(inLaws),
    ];
  }

  return [
    ...sortFn([...startTop, ...descendants]),
    ...sortInLaws(inLaws),
  ];
}

/**
 * Age sorts (Oldest to Youngest / Youngest to Oldest) never rank in-laws — the
 * client does not count them in anything involving age — so the in-laws-last
 * split runs whichever member tab is showing, not only on the all-members view:
 * descendants sort by `sortFn`, any in-laws on screen trail them A–Z. A tab that
 * holds no in-laws (Descendants, Start/Top) is sorted whole, exactly as before.
 */
export function sortDashboardMembersByAgeWithInLawsAlphabetical(
  members,
  sortFn,
  options = {}
) {
  return sortDashboardMembersWithInLawsLast(members, sortFn, "all", options);
}

/**
 * Oldest-to-youngest list order: Start/Top, then descendants, then in-laws (A–Z).
 */
export function sortDashboardMembersByOldestFamilyOrder(members, memberFilter = "all") {
  const startTop = members.filter((member) =>
    isDashboardStartOrTopOfLineMember(member)
  );
  const descendants = members.filter(
    (member) =>
      !isDashboardStartOrTopOfLineMember(member) &&
      !isDashboardListedInLawMember(member)
  );
  const inLaws = members.filter(
    (member) =>
      isDashboardListedInLawMember(member)
  );

  const sortOldest = sortDashboardMembersOldestToYoungest;

  if (memberFilter === "start-top") return sortOldest(startTop);
  if (
    memberFilter === "descendants" ||
    memberFilter === "descendants-living" ||
    memberFilter === "descendants-deceased"
  ) {
    return sortOldest(descendants);
  }
  // Related is the descendants list with the heads of line folded back in, so
  // it reads the same way as the all-members view minus its in-laws.
  if (
    memberFilter === "related" ||
    memberFilter === "related-living" ||
    memberFilter === "related-deceased"
  ) {
    return [...sortOldest(startTop), ...sortOldest(descendants)];
  }
  if (
    memberFilter === "in-laws" ||
    memberFilter === "in-laws-deceased" ||
    memberFilter === "in-relationship"
  ) {
    return sortOldest(members);
  }

  return [
    ...sortOldest(startTop),
    ...sortOldest(descendants),
    ...sortDashboardInLawsAlphabetically(inLaws),
  ];
}
